import {useCallback, useContext, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {userApi} from 'api';
import {ConfirmModal, ConnectionInfo, PredefinedNotifierType} from 'components';
import {UserContext} from 'context';
import {SocketEvent} from 'enums';
import {useApiData, useSocketEventCallback} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {UserDataStorage} from 'services/UserData';
import {UserConnectionInfo} from 'types';
import {apiUtils, logger} from 'utils';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.NEW_CONNECTION_MODAL;

export const NewConnectionModal: React.FC = () => {
    const {t} = useTranslation();

    const {socket} = useContext(UserContext);
    const [newConnections, setNewConnections] = useState<UserConnectionInfo[]>([]);
    const newConnection = useMemo(() => newConnections[0], [newConnections]);

    const addNewConnection = useCallback((connectionInfo: UserConnectionInfo) => {
        setNewConnections((connections) => [...connections, connectionInfo]);
    }, []);

    const approveNewConnection = useCallback(() => {
        setNewConnections(([, ...rest]) => rest);
    }, []);

    const buildNotifierErrorProps = useCallback((error) => {
        if (apiUtils.isResourceNotFoundError(error)) {
            return {title: t(T_KEYS.USER_NOT_ONLINE)};
        }
    }, [t]);

    const {send: disconnect} = useApiData(userApi.disconnectActiveSession, {
        onSuccess: approveNewConnection,
        notificationType: PredefinedNotifierType.Send,
        buildNotifierErrorProps
    });

    const handleModalClose = useCallback((confirm: boolean) => {
        if (!confirm) {
            disconnect(newConnection.socketId);
        } else {
            approveNewConnection();
        }
    }, [approveNewConnection, disconnect, newConnection?.socketId]);

    useSocketEventCallback(socket, SocketEvent.NewConnection, useCallback((connectionInfo: UserConnectionInfo) => {
        if (connectionInfo) {
            const data = UserDataStorage.getItem();
            if (data?.sessionId !== connectionInfo.sessionId) {
                addNewConnection(connectionInfo);
            }
        }
        logger.debug(`Received socket event: ${SocketEvent.NewConnection}`, connectionInfo);
    }, [addNewConnection]));

    return (
        <ConfirmModal
            title={t(T_KEYS.TITLE)}
            okText={t(T_KEYS.APPROVE_BTN)}
            cancelText={t(T_KEYS.DISCONNECT_BTN)}
            cancelBtnColor='attention'
            open={!!newConnection}
            maxWidth='xs'
            fullWidth
            onClose={handleModalClose}
        >
            <div className='tw-mb-1'>{t(T_KEYS.DESCRIPTION_1)}</div>
            <div className='tw-mb-6'>{t(T_KEYS.DESCRIPTION_2)}</div>
            {newConnection && <ConnectionInfo info={newConnection} />}
        </ConfirmModal>
    );
};
